import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    VStack,
    HStack,
    Box,
    Divider,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useSession } from 'store/session';

import { ExitIcon, SecurityLockIcon, PowerIcon } from 'components/icons/SecurityIcons';

interface LogoutConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLogout: () => void;
    primaryColor?: string;
}

export const LogoutConfirmationModal: React.FC<LogoutConfirmationModalProps> = ({
    isOpen,
    onClose,
    onLogout,
    primaryColor = '#6366f1',
}) => {
    const { t } = useTranslation();
    const { setIsV3Expired } = useSession();

    const handleLock = () => {
        setIsV3Expired(true);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
            <ModalContent borderRadius="2xl" overflow="hidden">
                <Box
                    bg="white"
                    _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.100' }}
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="2xl"
                    overflow="hidden"
                >
                    <ModalHeader pt={8} pb={4} px={8}>
                        <HStack spacing={3}>
                            <Box
                                w={10}
                                h={10}
                                bg="purple.50"
                                _dark={{ bg: 'whiteAlpha.100' }}
                                borderRadius="lg"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color={primaryColor}
                                flexShrink={0}
                            >
                                <ExitIcon width="20" height="20" />
                            </Box>
                            <Text fontSize="xl" fontWeight="bold">
                                {t('LOGOUT_MODAL.TITLE', { defaultValue: '¿Ya te vas?' })}
                            </Text>
                        </HStack>
                    </ModalHeader>

                    <ModalBody pb={8} px={8}>
                        <Text color="gray.500" _dark={{ color: 'gray.400' }} fontSize="sm">
                            {t('LOGOUT_MODAL.DESCRIPTION', { defaultValue: 'Elige cómo quieres proteger tu sesión antes de salir.' })}
                        </Text>

                        <VStack spacing={4} mt={8} align="stretch">
                            {/* Lock Option */}
                            <Button
                                variant="outline"
                                height="auto"
                                py={4}
                                px={4}
                                onClick={handleLock}
                                justifyContent="start"
                                textAlign="left"
                                borderWidth="1.5px"
                                borderColor="gray.200"
                                _dark={{ borderColor: 'whiteAlpha.200' }}
                                _hover={{
                                    bg: 'purple.50',
                                    borderColor: primaryColor,
                                    _dark: { bg: 'whiteAlpha.100', borderColor: primaryColor },
                                }}
                            >
                                <HStack spacing={4} w="full">
                                    <Box color={primaryColor}>
                                        <SecurityLockIcon width="24" height="24" />
                                    </Box>
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontWeight="bold" fontSize="md">
                                            {t('LOGOUT_MODAL.LOCK_TITLE', { defaultValue: 'Solo bloquear' })}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500" whiteSpace="normal">
                                            {t('LOGOUT_MODAL.LOCK_DESC', {
                                                defaultValue: 'Protege tu pantalla pero mantén tus documentos abiertos.',
                                            })}
                                        </Text>
                                    </VStack>
                                </HStack>
                            </Button>

                            {/* Logout Option */}
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                height="auto"
                                py={4}
                                px={4}
                                onClick={onLogout}
                                justifyContent="start"
                                textAlign="left"
                                _hover={{ bg: 'red.50', _dark: { bg: 'red.900' } }}
                            >
                                <HStack spacing={4} w="full">
                                    <Box>
                                        <PowerIcon width="24" height="24" />
                                    </Box>
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontWeight="bold" fontSize="md">
                                            {t('LOGOUT_MODAL.LOGOUT_TITLE', { defaultValue: 'Cerrar sesión' })}
                                        </Text>
                                        <Text fontSize="xs" color="red.500" whiteSpace="normal">
                                            {t('LOGOUT_MODAL.LOGOUT_DESC', {
                                                defaultValue: 'Termina tu sesión por completo. Todo se cerrará por seguridad.',
                                            })}
                                        </Text>
                                    </VStack>
                                </HStack>
                            </Button>
                        </VStack>
                    </ModalBody>

                    <Divider />

                    <ModalFooter bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} py={3}>
                        <Button variant="link" size="sm" onClick={onClose} color="gray.500" mx="auto">
                            {t(translations.COMMON.CANCEL, { defaultValue: 'Cancelar' })}
                        </Button>
                    </ModalFooter>
                </Box>
            </ModalContent>
        </Modal>
    );
};
